/**
 * Specialized utility functions for comparing Actuals data with history
 */

import { formatNumber } from '../utils/formatters';

/**
 * Normalize date values for comparison
 * Converts ISO dates to YYYY-MM-DD format for consistent comparison
 */
const normalizeValueForComparison = (value: any, field: string): any => {
    if ((field === 'startDate' || field === 'endDate') && value) {
        if (typeof value === 'string' && value.includes('T')) {
            return value.split('T')[0];
        }
        return value;
    }
    return value;
};

export interface ShiftComparison {
    shiftNumber: string;
    status: 'unchanged' | 'modified' | 'deleted' | 'new';
    changes: Record<string, { base: any; requested: any }>;
    originalData?: any;
    historyData?: any;
}

export interface TopLevelChanges {
    venue?: { base: string; requested: string };
    address?: { base: string; requested: string };
    clientPo?: { base: string; requested: string };
    title?: { base: string; requested: string };
    billTo?: { base: string; requested: string };
}

/**
 * Compare Actuals shifts with lastEventHistory
 * Logic: Strict matching by ID and Shift Number.
 */
export const compareShifts = (
    currentShifts: any[],
    historyShifts: any[] | undefined
): ShiftComparison[] => {
    if (!historyShifts || historyShifts.length === 0) {
        return currentShifts.map(shift => ({
            shiftNumber: shift.shiftNumber || '',
            status: 'unchanged',
            changes: {},
            originalData: shift
        }));
    }

    const result: ShiftComparison[] = [];
    const processedHistoryIndices = new Set<number>();

    const findHistoryMatch = (currentShift: any) => {
        const shiftNum = String(currentShift.shiftNumber || '').trim();
        const currentId = currentShift.id;

        // 1. Priority: Match by Numeric ID (using shiftId if available in history)
        if (typeof currentId === 'number') {
            const idMatch = historyShifts.findIndex((sh, i) =>
                !processedHistoryIndices.has(i) &&
                (sh.shiftId === currentId || sh.id === currentId)
            );
            if (idMatch !== -1) return idMatch;
        }

        // 2. Secondary: Match by shiftNumber
        if (shiftNum && !shiftNum.startsWith('new-hist-') && !String(shiftNum).startsWith('dup-')) {
            const numMatch = historyShifts.findIndex((sh, i) =>
                !processedHistoryIndices.has(i) &&
                String(sh.shiftNumber || '').trim().toLowerCase() === shiftNum.toLowerCase()
            );
            if (numMatch !== -1) return numMatch;
        }

        // 3. Tertiary: Fuzzy Match by content
        const fuzzyMatch = historyShifts.findIndex((sh, i) => {
            if (processedHistoryIndices.has(i)) return false;

            const shDate = normalizeValueForComparison(sh.startDate, 'startDate');
            const curDate = normalizeValueForComparison(currentShift.startDate, 'startDate');

            return (
                shDate === curDate &&
                String(sh.position || '').toLowerCase() === String(currentShift.position || '').toLowerCase() &&
                Number(sh.quantity) === Number(currentShift.quantity) &&
                String(sh.startTime || '').trim() === String(currentShift.startTime || '').trim()
            );
        });

        return fuzzyMatch;
    };

    for (const currentShift of currentShifts) {
        const matchIndex = findHistoryMatch(currentShift);

        if (matchIndex === -1) {
            result.push({
                shiftNumber: currentShift.shiftNumber || '',
                status: 'deleted',
                changes: {},
                originalData: currentShift
            });
        } else {
            processedHistoryIndices.add(matchIndex);
            const historyShift = historyShifts[matchIndex];
            const changes: Record<string, { base: any; requested: any }> = {};
            const fieldsToCompare = [
                'startDate', 'endDate', 'position', 'quantity', 'startTime', 'endTime',
                'notes', 'rateType', 'callType', 'mealBreak', 'rate', 'desiredRate',
                'registerHours', 'overTimeHours', 'DoubleTimeHours', 'addOns', 'miscFees'
            ];

            for (const field of fieldsToCompare) {
                const baseVal = normalizeValueForComparison(currentShift[field], field);
                const reqVal = normalizeValueForComparison(historyShift[field], field);

                if (baseVal !== reqVal && !(baseVal == null && reqVal == null)) {
                    changes[field] = {
                        base: currentShift[field],
                        requested: historyShift[field]
                    };
                }
            }

            let status: any = Object.keys(changes).length > 0 ? 'modified' : 'unchanged';
            const shiftNum = String(historyShift.shiftNumber || currentShift.shiftNumber || '').trim();

            result.push({
                shiftNumber: shiftNum,
                status,
                changes,
                originalData: currentShift,
                historyData: historyShift
            });
        }
    }

    historyShifts.forEach((hShift, index) => {
        if (!processedHistoryIndices.has(index)) {
            result.push({
                shiftNumber: hShift.shiftNumber || `new-hist-${index}`,
                status: 'new',
                changes: {},
                historyData: hShift
            });
        }
    });

    return result;
};

export const compareTopLevel = (
    currentData: any,
    historyData: any
): TopLevelChanges => {
    const changes: TopLevelChanges = {};
    const fieldsToCompare: (keyof TopLevelChanges)[] = ['venue', 'address', 'clientPo', 'title', 'billTo'];

    for (const field of fieldsToCompare) {
        if (currentData[field] !== historyData?.[field]) {
            changes[field] = {
                requested: historyData?.[field] || 'N/A',
                base: currentData[field] || 'N/A'
            };
        }
    }

    return Object.keys(changes).length > 0 ? changes : {};
};

export const getFieldDisplayName = (field: string): string => {
    const displayNames: Record<string, string> = {
        startDate: 'Start Date',
        endDate: 'End Date',
        position: 'Position',
        quantity: 'Quantity',
        startTime: 'Start Time',
        endTime: 'End Time',
        notes: 'Notes',
        rateType: 'Rate Type',
        callType: 'Call Type',
        mealBreak: 'Meal Break',
        rate: 'Rate',
        desiredRate: 'Desired Rate',
        registerHours: 'ST Hours',
        overTimeHours: 'OT Hours',
        DoubleTimeHours: 'DT Hours',
        addOns: 'Per Diem',
        miscFees: 'Misc Fees',
        totals: 'Total',
        venue: 'Venue',
        address: 'Event Location',
        clientPo: 'Client PO',
        title: 'Job #',
        billTo: 'Bill To'
    };

    return displayNames[field] || field;
};

export const formatValueForDisplay = (value: any, field: string): string => {
    if (value === null || value === undefined) return 'N/A';
    if (field.includes('Date')) return new Date(value).toLocaleDateString();

    if (field === 'rate' || field === 'desiredRate' || field === 'addOns' || field === 'miscFees' || field === 'totals') {
        if (typeof value === 'string' && value.startsWith('$')) return value;
        return `$${formatNumber(value)}`;
    }

    if (field === 'startTime' || field === 'endTime') {
        const formatTime = (time: string) => {
            if (!time || !time.includes(':')) return time;
            const [hours, minutes] = time.split(':');
            const h = parseInt(hours);
            const m = minutes.split(' ')[0];
            const ampm = h >= 12 ? 'PM' : 'AM';
            const hh = h % 12 || 12;
            return `${hh}:${m} ${ampm}`;
        };

        if (typeof value === 'string' && value.includes('-')) {
            return value.split('-').map(t => formatTime(t.trim())).join('-');
        }
        return formatTime(String(value));
    }

    return String(value);
};
